import Link from 'next/link';
import { RecordInterface, Input } from '@components/common/Forms';
import {
  Dispatch, forwardRef, memo, SetStateAction, MouseEvent, useEffect, useRef, useState, useCallback,
} from 'react';
import { ApplicantStatusEnum, OperationJob, StatusChangeActorEnum } from '@type/v1-api-types';
import { CardAnimations } from '@type/card';
import { useTranslation } from 'next-i18next';
import {
  Anchor, Avatar, Button, Card, ImageBj, Modal, Tooltip,
} from '@components/common';
import {
  ChatBubbleBottomCenterIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon as StarIconSolid,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/solid';
import {
  BriefcaseIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
  StarIcon as StarIconOutline,
} from '@heroicons/react/24/outline';
import { StoriesBadgeVideo } from '@components/templates/stories/StoriesBadgeVideo';
import { useTalentCardActions } from '@components/templates/employer/cards-lists/hooks/useTalentCardActions';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { clsx } from 'clsx';
import { locationPreferencesKeys } from '@utils/resume/job-type-preferences';
import { dataTestId } from '@services/data-test-id';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { BaseTrackingEventData, trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useInView } from 'react-intersection-observer';
import { useUnblockOperations } from '@components/templates/cv/hooks/useUnblockOperations';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';

import { getI18nLinkHref } from '@services/i18nPaths';
import {
  ApplicantBadgePill, ApplicantInterest,
} from './partials';

// Interface
// ******************************************
export interface TalentCardFormProps {
  record: RecordInterface,
  name: string,
  setSelectAll?: Dispatch<SetStateAction<boolean | undefined>>,
  getSelectedApplicants?: () => string[],
}

interface TalentCardProps {
  cardData: ApiSchema<'ResumeCardData'> & { hideStatus?: boolean; },
  locale: Locale,
  className?: string,
  columnName?: string,
  animation?: CardAnimations,
  disableFolderStatus?: boolean,
  handleRowUnchecked?: (columnName: string) => void;
  trackingData? : BaseTrackingEventData;
  internalAgent?: boolean,
  showActions?: boolean,
  revalidatePage?: (userSlug: string) => void,
  jobSlug?: string,
  jobInfo?: ApiSchema<'ListReadJob'>;
  applicant?: TalentCardFormProps,
  openTalentModal?: (userSlug: string, as?: string) => void,
  trackingId?: string,
  isPassiveRecommendation?: boolean | 0 | 1,
  handleStatusChangeOnCard?: (statusId: string | number, cvSlug: string) => void,
  mode?: 'default' | 'alumni',
  onView?: (talentSlug: string) => void,
  isMc?: boolean,
}


/**
 * @description TalentCard component. To be used in the TalentSearch page.
 *
 * - cardData: *ApiUserTalentItem* - The data for the card. Required.
 * - locale - *Locale* - The current app locale.
 * - isCompany: *boolean* - Check whether the authenticated user is company or not. Optional.
 * - className: *string* - Additional classes for the card. Optional.
 * - animation: *CardAnimations* - The animation for the card. Optional. Default is 'rise'.
 * - internalAgent: *boolean* - If true, the card will have a special button. Optional. Default is undefined.
 * - jobSlug: *string* - The job slug. Optional. Default is undefined. Needed for the CV opening in the job context.
 * - jobInfo: The job information.
 * - applicant: *object* - The object containing the record function and the name of the applicant. Optional. Default is undefined.
 * - openTalentModal: *function* - If provided, the CV will open in a modal. Optional.
 * - revalidatePage: *function* - The function to revalidate the page. Optional. Default is undefined.
 * - showActions: *boolean* - If true, the card will show the actions. Optional. Default is true.
 * - trackingId: *string* - The request id from the page in the list (each page has a different apiRequestId string). Used for tracking.
 * - trackingData: *string* - The object for getting tracking details from original list
 * - isPassiveRecommendation: *boolean* - If true, we don't count towards the Cv view limit. Optional. Default is false.
 * - handleStatusChangeOnCard: *function* - Function to handle the status change on the card. Optional. Default is undefined.
 * - mode: *'default' | 'alumni'* - The mode of the card. Default is 'default'.
 *
 * Applicant object:
 * - applicant.record: *RecordInterface* - The record function from useBjForm. Required.
 * - applicant.name: *string* - The name of the form group. Required.
 * - applicant.setSelectAll: *Dispatch<SetStateAction<boolean | undefined>>* - The function to set the 'select all' checkbox. Optional. Default is undefined.
 */
export const TalentCard = forwardRef<HTMLDivElement, TalentCardProps>((props, ref) => {
  // Destructure props
  const {
    cardData,
    locale,
    disableFolderStatus = false,
    className,
    animation = 'rise',
    internalAgent,
    showActions = true,
    revalidatePage,
    jobSlug,
    jobInfo,
    applicant,
    trackingData,
    openTalentModal,
    trackingId,
    handleRowUnchecked,
    columnName,
    isPassiveRecommendation = false,
    handleStatusChangeOnCard,
    mode = 'default',
    onView,
    isMc = false,
  } = props;

  const {
    record,
    name,
    setSelectAll,
    getSelectedApplicants,
  } = applicant || {};

  // Translation
  const { t } = useTranslation('common');


  // Helper variables
  // *****************************************
  const leadExperience = cardData?.leadExperience?.title;
  const talentName = cardData?.privateInformation?.name;
  const talentSlug = cardData?.slug || cardData.id || '';
  const talentCity = (cardData?.location)?.split(', ')[0];
  const talentPreferences = cardData?.preferences?.filter((item) => item !== t(locationPreferencesKeys.relocation))?.join(', ') || '';
  const availableForRelocation = cardData?.preferences?.some((item) => item === t(locationPreferencesKeys.relocation));
  const hasVideoCv = !!cardData?.privateInformation?.videoUrl;
  const hasAiInterview = cardData.hasVideos;
  const hasTrackedView = useRef<boolean>(false);

  // Prevent duplicate flags
  const spokenLanguages = cardData?.spokenLanguages
    ? [...new Map(cardData.spokenLanguages.map((lang) => [lang.shortName, lang])).values()]
    : undefined;

  // Add query params to the card link. Just make them optional object keys, don't pollute the URL if not needed.
  const cardLinkUrlParams = buildSearchParams({
    ...(trackingId ? { rid: trackingId } : {}),
    ...(isPassiveRecommendation ? { isPassiveRecommendation: 1 } : {}),
  });


  const isAddedToShortlist = (!jobSlug && cardData?.interviewFolder?.some((item) => (item.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.SHORTLIST))
    || (jobSlug && (cardData.application?.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.SHORTLIST);

  // Common classes
  // *****************************************
  const cardClasses = clsx(
    'group/talentCard relative overflow-hidden rounded-md !border-none bg-surface !shadow-none',
    className,
    {
      'border-y-2 border-x border-pink': cardData?.hasUserSubscription,
      'shadow-xs': cardData?.application?.isNew,
    },
  );
  const titleClasses = 'mt-1 mb-2.5 font-bold leading-tight text-lg';
  const cvCompletionBadgeClasses = '!bg-secondary z-2 text-white !outline-surface !ring-surface !bottom-auto top-0 !-left-1';
  const cardFooterClasses = 'relative z-2 can-hover:h-0 can-hover:transition-all can-hover:duration-200 can-hover:opacity-0 group-hover/talentCard:opacity-100 group-hover/talentCard:-translate-y-13';
  const cardActionWrapperClasses = `h-13 bg-surface-50 grid items-center divide-x divide-input ${disableFolderStatus ? 'grid-cols-1' : 'grid-cols-2'}`;
  const cardActionButtonClasses = 'group hover:!no-underline hover:enabled:text-ink focus-visible:text-ink';
  const cardActionButtonIconClasses = 'w-5 h-5 mr-2';
  const cardActionButtonIconSecondaryClasses = 'transition-all duration-200 text-primary group-hover:group-enabled:text-primary';


  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const isAuthenticated = !!headerInfoData || !!jobSlug;

  let cardLink = getI18nLinkHref(`/cv/${talentSlug}${cardLinkUrlParams}`, locale);
  if (jobSlug && isMc) {
    cardLink = getI18nLinkHref(`/mc/job/${jobSlug}/cv/${talentSlug}${cardLinkUrlParams}`, locale);
  } else if (jobSlug) {
    cardLink = getI18nLinkHref(`/job/${jobSlug}/cv/${talentSlug}${cardLinkUrlParams}`, locale);
  }

  // When openTalentModal is provided, the CV opens in a modal on the current page.
  // Use a href with ?selectedCvSlug= so that middle-click (new tab) lands on the same page with the modal auto-opened.
  const cardHref = openTalentModal && typeof window !== 'undefined'
    ? `${window.location.pathname}${buildSearchParams({ ...Object.fromEntries(new URLSearchParams(window.location.search)), selectedCvSlug: talentSlug })}`
    : cardLink;

  // Handle the CV opening (normal or in a modal, depending on the props)
  // If we have a jobSlug, we open the CV in the job context
  // *****************************************
  const handleCvOpening = (e: MouseEvent<HTMLAnchorElement>) => {
    if (openTalentModal) {
      e.preventDefault();
      openTalentModal(talentSlug, cardLink);
    } else if (!isAuthenticated) {
      window.location.href = '/login';
    }
  };


  const renderIconByScore = (score:number) => {
    if (score === 1) {
      return <HandThumbUpIcon className="size-5 min-w-5 text-secondary" />;
    }
    if (score === 2) {
      return <HandThumbUpIcon className="size-5 min-w-5 text-warning" />;
    }
    return <HandThumbDownIcon className="size-5 min-w-5 text-error" />;
  };

  const [activeJobContext, setActiveJobContext] = useState<OperationJob>({
    jobId: Number(jobInfo?.jobId),
    title: jobInfo?.title ?? '',
    jobSlug: jobSlug ?? '',
  });

  // Profile operations
  // ***********************************************
  const {
    displaySendMessageModal,
    isLoadingSendMessage,
    toggleSendMessageModal,
    isLoadingOperationJob,
    saveToShortlistAction,
    shouldOpenMessageModal,
  } = useTalentCardActions({
    jobSlug: activeJobContext?.jobSlug,
    jobFolderId: activeJobContext?.jobId,
    jobTitle: activeJobContext?.title,
    userSlug: cardData?.slug as string,
    locale: locale ?? 'ro',
    revalidatePage: revalidatePage ?? (() => {}),
    hasInterviewFolder: !!cardData?.hasInterviewFolder,
    interviewFolders: cardData?.interviewFolder,
    rid: trackingId,
    handleStatusChangeOnCard,
    isPassiveRecommendation: Boolean(isPassiveRecommendation),
  });

  const {
    openPricingInformationModal,
    confirmModalOpen,
    unblockCv,
    cancelUnlock,
    hasSkipUnlockConfirm,
  } = useUnblockOperations({
    userIdToUnlock: Number(cardData.id),
    jobSlug: activeJobContext?.jobSlug,
    jobTitle: activeJobContext?.title,
    jobFolderId: Number(activeJobContext?.jobId),
  });

  const { ref: inViewRef, inView } = useInView({ threshold: 1, triggerOnce: true });

  // Merge forwarded ref with inViewRef
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    inViewRef(node);
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref, inViewRef]);

  // Added tracking seen event
  useEffect(() => {
    if (trackingData?.disableTracking) return;
    if (inView && !hasTrackedView.current) {
      trackEvent({
        eventType: 'view',
        entityType: 'resume',
        entityId: talentSlug ?? '',
        viewContext: 'card',
        ...(userId && { userId: userId.toString() }),
        ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
        ...(trackingData?.listType && { listType: trackingData.listType }),
        ...(trackingData?.position && { position: trackingData.position }),
        metadata: [
          ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
          ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
        ],
      });
      onView?.(talentSlug);
      hasTrackedView.current = true;
    }
  }, [inView, talentSlug, userId, trackingData, onView, jobSlug]);

  const continueUnlockProgress = () => {
    trackEvent({
      eventType: 'action',
      entityType: 'resume',
      entityId: cardData?.slug ?? '',
      viewContext: 'card',
      action: 'contact',
      ...(trackingData?.listType && { listType: trackingData?.listType }),
      ...(trackingData?.position && { position: trackingData?.position }),
      ...(userId && { userId: userId.toString() }),
      metadata: [
        ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
        ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
      ],
    });
    void unblockCv({
      type: 'message',
      onSuccess: () => {
        toggleSendMessageModal();
        if (cardData?.locked) {
          revalidatePage?.(cardData?.slug ?? '');
        }
      },
    });
    // openMessageWithJobFolder();
  };


  // Render component
  // *****************************************
  return (
    <Card ref={mergedRef} animation={animation} className={cardClasses} {...dataTestId('cvSearchPage.card.default')}>
      <Card.Body className="relative flex gap-4 px-4 py-6">
        {/* CARD LINK */
            talentSlug && (
              <Link
                href={cardHref}
                prefetch={false}
                onClick={(e) => {
                  trackEvent({
                    eventType: 'click',
                    entityType: 'resume',
                    entityId: cardData?.slug ?? '',
                    viewContext: 'card',
                    ...(trackingData?.listType && { listType: trackingData.listType }),
                    ...(trackingData?.position && { position: trackingData.position }),
                    ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
                    ...(userId && { userId: userId.toString() }),
                    metadata: [
                      ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
                      ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
                    ],

                  });
                  handleCvOpening(e);
                }}
                className="absolute inset-0 z-1 flex"
              >
                {/* Tooltip for premium users on the talent list (no job context) */
                  cardData?.hasUserSubscription && !jobSlug ? (
                    <Tooltip
                      tooltipText={t('premium.cv.badge.tooltip')}
                      position="top"
                      floating
                      className="block size-full"
                      triggerContainerClass="block w-full h-full"
                      colorScheme="pink"
                    >
                      <span className="opacity-0">
                        {leadExperience || talentName}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="opacity-0">
                      {leadExperience || talentName}
                    </span>
                  )
                }
              </Link>
            )
          }


        {/* APPLICANT checkbox; uncheck the 'select all' checkbox on click */
            record && name && (
              <label
                htmlFor="applicantSlug"
                aria-label={t('global.default.select.option')}
                title={t('global.default.select.option')}
                className="absolute -top-1 right-0 z-2 cursor-copy p-1"
              >
                <Input
                  defaultValue={cardData?.slug}
                  type="checkbox"
                  className="cursor-copy text-secondary ring-3 ring-surface checked:bg-secondary"
                  {...record(name, {
                    id: `${name}_${cardData?.slug}`,
                    valueAsArray: true,
                    columnName,
                  })}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (setSelectAll) setSelectAll(undefined);
                    if (getSelectedApplicants) getSelectedApplicants();
                    if (!e.target.checked && columnName) {
                      handleRowUnchecked?.(columnName);
                    }
                  }}
                />
              </label>
            )
          }


        {/* DESCRIPTION */}
        <div className="grow">
          {(internalAgent || cardData?.activityStatus || cardData?.interest) && (
          <div className="mb-2 flex items-center gap-3 divide-x divide-ink/15 text-sm text-ink-light">
            {/* Activity status; hide it in applicants' lists */
                  !jobSlug && cardData?.activityStatus && (
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="inline-block size-2.5 rounded-full bg-secondary" />
                      {cardData?.activityStatus}
                    </span>
                  )
                }

            {/* Interest (i.e.: applied at date); show only in the job context */
                  jobSlug && cardData?.interest && (
                    <ApplicantInterest type={cardData.interest?.type} value={cardData.interest?.value} />
                  )
                }

            {/* Internal agent button */
                  internalAgent && cardData?.id && (
                    <span {...cardData?.activityStatus || cardData?.interest ? { className: 'pl-3' } : {}}>
                      <Anchor
                        href={`https://intranet.bestjobs.eu/user/detail/${cardData?.id}`}
                        styling="text"
                        size="sm"
                        color="primary-light"
                        rounding="none"
                        aria-label="internal-agent"
                        title="internal-agent"
                        className="relative z-2"
                        target="_blank"
                      >
                        <ArrowTopRightOnSquareIcon className="size-4.5" />
                      </Anchor>
                    </span>
                  )
                }

            {cardData?.application && (
              cardData.application.isNew ? (
                <EyeSlashIcon className="size-4 !border-0" />
              ) : (
                <EyeIcon className="size-4 !border-0" />
              )
            )}
          </div>
          )}

          {/* The main title of the card: Lead experience or name (if CV is unlocked) */
              (talentName || leadExperience) && (
                <div className={titleClasses}>{talentName || leadExperience}</div>
              )
            }

          {/* The rest of the description */}
          <div className="text-sm text-ink-medium">
            {/* Lead experience if CV unlocked - that means the title is the name */
                talentName && leadExperience && (
                  <div className="font-bold">{leadExperience}</div>
                )
              }
            {/* Lead experience company: hidden when alumni */
                mode === 'default' && cardData?.leadExperience?.company && (
                  <div>{`@ ${cardData?.leadExperience?.company}`}</div>
                )
              }
            {/* Lead experience period */
                cardData?.leadExperience?.period && (
                  <div>
                    {mode === 'default' && `${t('card.experience.total.domain')} `}
                    <strong>{cardData?.leadExperience?.period}</strong>
                  </div>
                )
              }
            {
                mode === 'alumni' && (
                  <div className="mb-0.5 mt-2.5 flex gap-1 text-xs">
                    <BriefcaseIcon className="mt-px size-4 shrink-0" />
                    <span>{`${t('cv.section.label.professional_experience')}: ${cardData?.experienceSummary}`}</span>
                  </div>
                )
              }
            {/* Location (city only) and preferences */
                (talentCity || talentPreferences) && (
                  <div className="mb-0.5 mt-2.5 flex gap-1 text-xs">
                    <MapPinIcon className="mt-px size-4 shrink-0" />
                    <span className="line-clamp-2 grow">
                      {talentCity}
                      {/* Location preferences */}
                      {mode === 'default' && (
                        <>
                          {availableForRelocation && (
                            <>
                              {', '}
                              <strong>
                                {t(locationPreferencesKeys.relocation)}
                              </strong>
                            </>
                          )}
                          {talentPreferences && ', '}
                          {talentPreferences}
                        </>
                      )}
                    </span>
                  </div>
                )
              }
          </div>
        </div>

        {/* AVATAR - make room for buck checkbox when in job context */}
        <div className="shrink-0">
          {/* Talent avatar */}
          <div className="relative z-2">
            {/* Badge for 100% Cv completion */
                cardData?.cvCompletion === 100 && (
                  <Avatar.Badge withRing iconName="star" className={cvCompletionBadgeClasses} />
                )
              }

            {hasAiInterview && <StoriesBadgeVideo isAuthenticated={isAuthenticated} openTalentModal={openTalentModal} slug={cardData?.slug} />}

            <Avatar
              imgPath={cardData?.imgUrl}
              imgFolder="resume_image"
              extractImageName
              className={`mx-auto ${hasVideoCv ? '!outline-2 !outline-offset-3 !outline-secondary' : ''}`}
              alt={talentName}
              name={talentName}
              withRing={hasVideoCv}
            />

          </div>

          {/* Language flags */
              spokenLanguages && spokenLanguages.length && (
                <div className="mt-3 flex items-center justify-center gap-1.5">
                  {spokenLanguages.slice(0, 2).map((lang) => (
                    <ImageBj
                      key={`${lang.shortName}-${cardData.slug}-${cardData?.id}`}
                      src={lang.flagUrl || ''}
                      alt=""
                      width={16}
                      height={16}
                      className="size-4 shrink-0"
                    />
                  ))}
                  {/* Show if we have more than 2 languages */
                    spokenLanguages.length > 2 && (
                      <span className="text-xs text-ink-medium">{`+${spokenLanguages.length - 2}`}</span>
                    )
                  }
                </div>
              )
            }

          {/* Application status */
              cardData?.application && (
                <div className="mt-2 text-center">
                  {!cardData?.hideStatus && (
                  <ApplicantBadgePill
                    statusId={cardData.application?.statusId}
                    statusChangeActor={cardData.application?.statusChangeActor as StatusChangeActorEnum | undefined}
                  />
                  )}
                </div>
              )
            }
        </div>

      </Card.Body>

      {/* Candidate Evaluation */}
      {
        cardData?.evaluation && (
          <div className="px-3 pb-3">
            <div className="flex items-start gap-2 rounded-md bg-surface-50 p-2">
              {renderIconByScore(
                (cardData?.evaluation as { status?: number })?.status ?? 3,
              )}

              <div className="line-clamp-3 text-tiny font-thin">
                {cardData?.evaluation?.summary}
              </div>
            </div>
          </div>
        )
      }


      {/* CARD ACTIONS */}
      {
          (showActions) && (
            <Card.Footer className={cardFooterClasses}>
              <div className={cardActionWrapperClasses}>
                {/* Save to shortlist */}

                {!disableFolderStatus
                  && (
                  <Button
                    styling="text"
                    size="sm"
                    color="ink-medium"
                    rounding="none"
                    className={cardActionButtonClasses}
                    isLoading={isLoadingOperationJob && !shouldOpenMessageModal}
                    onClick={() => {
                      trackEvent({
                        eventType: 'action',
                        entityType: 'resume',
                        entityId: cardData?.slug ?? '',
                        viewContext: 'card',
                        action: 'save',
                        ...(trackingData?.listType && { listType: trackingData?.listType }),
                        ...(trackingData?.position && { position: trackingData?.position }),
                        ...(trackingData?.listUuid && { listUuid: trackingData?.listUuid }),
                        ...(userId && { userId: userId.toString() }),
                      });
                      saveToShortlistAction();
                    }}
                    {...dataTestId('cvSearchPage.card.saveToShortlistAction')}
                  >
                    {!(isLoadingOperationJob && !shouldOpenMessageModal) && (
                      isAddedToShortlist
                        ? (
                          <StarIconSolid
                            className={clsx(cardActionButtonIconClasses, cardActionButtonIconSecondaryClasses)}
                          />
                        )
                        : (
                          <StarIconOutline
                            className={clsx(cardActionButtonIconClasses, cardActionButtonIconSecondaryClasses, 'stroke-2')}
                          />
                        )
                    )}
                    <span>{t('talent-card.v2.action.shortlist')}</span>
                  </Button>
                  )}


                {/* Send Message action */}
                <Button
                  styling="text"
                  size="sm"
                  color="ink-medium"
                  rounding="none"
                  className={cardActionButtonClasses}
                  isLoading={isLoadingSendMessage || (isLoadingOperationJob && shouldOpenMessageModal)}
                  disabled={isLoadingSendMessage || !cardData.slug}
                  onClick={() => {
                    if (cardData?.locked) {
                      openPricingInformationModal();
                    } else if (hasSkipUnlockConfirm()) {
                      continueUnlockProgress();
                    } else {
                      continueUnlockProgress();
                    }
                  }}
                  {...dataTestId('cvSearchPage.card.unlockWithMessage')}
                >
                  {!(isLoadingSendMessage || (isLoadingOperationJob && shouldOpenMessageModal)) && (
                    <ChatBubbleBottomCenterIcon className={cardActionButtonIconClasses} />
                  )}
                  <span>
                    {t('talent-card.v2.action.message')}
                    {' '}
                  </span>
                </Button>

              </div>
            </Card.Footer>
          )
      }

      {/* Send message modal */}
      {cardData && cardData.slug && (
        <>
          <Modal
            isOpen={displaySendMessageModal}
            body={(
              <SendMessageModalContent
                imageUrl={cardData.imgUrl || ''}
                candidateName={cardData?.privateInformation?.name || ''}
                onSendSuccess={() => toggleSendMessageModal()}
                onClose={() => {
                  toggleSendMessageModal();
                }}
                slug={cardData.slug}
                trackingId={trackingId}
                locale={locale}
              />
            )}
            onClose={() => {
              toggleSendMessageModal();
            }}
          />

          <Modal
            size="sm"
            isOpen={confirmModalOpen}
            body={(
              <CustomConfirmModalContent
                jobContextDetail={{
                  jobFolderId: activeJobContext.jobId,
                  jobSlug: activeJobContext.jobSlug,
                  title: activeJobContext.title,
                }}
                isPassiveRecommendation={Boolean(isPassiveRecommendation)}
                userSlug={cardData?.slug}
                updateActiveJobFolderContext={(item) => {
                  setActiveJobContext(item);
                }}
                jobSlug={jobSlug}
                onSendSuccess={(val) => {
                  if (val) {
                    continueUnlockProgress();
                    cancelUnlock();
                  } else {
                    cancelUnlock();
                  }
                }}
              />
            )}
            onClose={() => null}
          />
        </>

      )}


    </Card>
  );
});


/**
      * @description Memoized TalentCard component. To be used in the infinite lists.
      */
export const TalentCardMemo = memo(TalentCard);
